import { foldApostrophes } from "./scenario";

/**
 * R8.3 (CLAUDE.md sections 0 and 7, B3, B13, F-21, TA-84). The closed list of
 * EXPLICIT opt-outs: a message that is, on its own, a customer telling the
 * shop to stop. The suppression detector (packages/agents
 * suppressionDetectHandler.ts) writes `suppression.added` on a match with no
 * person and no model in the loop, so the list is short and literal.
 *
 * PRECISION BEATS RECALL (B13). Suppression beats a live conversation (the
 * arbiter reads the list first), so a wrong match silences a customer who may
 * be mid-purchase. A message matches only when, once case, whitespace, the
 * sentence marks . , ! and a little politeness filler are set aside, the WHOLE
 * message is one phrase below. Therefore:
 *  - a phrase inside a longer sentence is not explicit ("stop by on Saturday",
 *    "remove me from the waitlist for the gold one");
 *  - a negated phrase is not explicit ("don't stop messaging me");
 *  - a quoted phrase is not explicit (`I didn't say "stop"`, `"stop"`): a
 *    quote mark is a leftover character, not punctuation set aside;
 *  - a question is not explicit ("Stop?"): a question mark is a leftover too;
 *  - an emoji, an emoticon or any other leftover character is not explicit.
 * This list is explicit-only. The detector's automatic set is this list UNION
 * pipeline OPT_OUT_RULES, which still accepts quote marks ("stop"), the lead
 * filler hi/hello/hey/ok/okay/kindly/just/thanks and the tails now/ok/okay,
 * again and "from your list"; that is what makes "ok stop", "just stop",
 * "stop now", "kindly stop" and a quoted "stop" automatic (pinned in the agents
 * suppressionDetectHandler tests). Anything that does not match here goes
 * to the detector's existing path (pipeline `detectSuppressionSignal`, and the
 * classifier for an ambiguous read) unchanged.
 *
 * CODES ONLY (section 10). The result is a phrase id. No word of the message
 * is returned, so none can reach an event or an alert.
 *
 * T3 (section 3): this list is a guardrail. Editing it is a person's change,
 * reviewed like code; no agent writes it.
 */

export interface ExplicitOptOutPhrase {
  /** A short machine code, `explicit_phrase.<name>`. It is what the ledger records. */
  readonly id: string;
  /** The phrase, lower case, apostrophes inside words removed ("don't" reads "dont"). */
  readonly phrase: string;
}

export const EXPLICIT_OPT_OUT_PHRASES: readonly ExplicitOptOutPhrase[] = Object.freeze([
  { id: "explicit_phrase.stop", phrase: "stop" },
  { id: "explicit_phrase.unsubscribe", phrase: "unsubscribe" },
  { id: "explicit_phrase.stop_messaging_me", phrase: "stop messaging me" },
  { id: "explicit_phrase.stop_texting_me", phrase: "stop texting me" },
  { id: "explicit_phrase.dont_contact_me", phrase: "dont contact me" },
  { id: "explicit_phrase.do_not_contact_me", phrase: "do not contact me" },
  { id: "explicit_phrase.remove_me", phrase: "remove me" },
  { id: "explicit_phrase.take_me_off", phrase: "take me off" },
  { id: "explicit_phrase.leave_me_alone", phrase: "leave me alone" },
  { id: "explicit_phrase.no_more_messages", phrase: "no more messages" },
].map((p) => Object.freeze(p)));

/** Longer than this and the message is not one short phrase; it is never read as explicit. */
const MAX_EXPLICIT_LENGTH = 120;

/** Politeness set aside at the start and the end. Deliberately narrow: "ok stop" is not on core's list (V6 still suppresses it). */
const FILLER_LEAD = /^(?:(?:please|pls)\s+)+/;
const FILLER_TAIL = /(?:\s+(?:please|pls|thanks|thank you))+$/;

/** After normalization, only letters, spaces and the sentence marks set aside may remain. */
const ONLY_WORDS_AND_MARKS = /^[a-z\s.,!]*$/;

export interface ExplicitOptOutMatch {
  readonly phraseId: string;
}

/** The phrase id when the whole message is one explicit opt-out, else null. Pure and deterministic. */
export function matchExplicitOptOut(text: string): ExplicitOptOutMatch | null {
  if (text.length > MAX_EXPLICIT_LENGTH) return null;
  const lowered = foldApostrophes(text).toLowerCase();
  // An apostrophe inside a word is spelling ("don't"); anywhere else it is a quote mark and stays a leftover.
  const unapostrophed = lowered.replace(/(?<=[a-z])'(?=[a-z])/g, "");
  if (!ONLY_WORDS_AND_MARKS.test(unapostrophed)) return null;
  const whole = unapostrophed
    .replace(/[.,!]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(FILLER_LEAD, "")
    .replace(FILLER_TAIL, "")
    .trim();
  if (whole.length === 0) return null;
  const hit = EXPLICIT_OPT_OUT_PHRASES.find((p) => p.phrase === whole);
  return hit ? { phraseId: hit.id } : null;
}
